/* PersonalizedMenuCard — AI-генерирано дневно меню от целия HI профил
 * (iter421.5). NutriBot + EndoBot ensemble produce structured plan.
 *
 * Sections:
 *   • Summary (1-2 изречения от NutriBot)
 *   • Meal Plan grid (закуска / обяд / вечеря / снак) с DIL est_g
 *   • Foods to Favor (5 items с reason)
 *   • Foods to Limit (3 items с empathetic reason)
 *   • Weekly Experiment
 *   • EndoBot panel (приоритети, timing, lab to monitor)
 *
 * Cache: 24h. Save-to-favorites бутон на всяка храна в Favor секцията.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ChefHat, Sparkles, Sun, Sunset, Moon, Cookie, Heart, Hand,
  RefreshCw, Loader2, Bookmark, FlaskConical, Clock, Info, Stethoscope,
  Zap, Activity, Flame, CheckCircle2, TrendingUp, Plus, X, Lightbulb, Search,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const MEAL_META = {
  breakfast: { icon: Sun,    bg: "Закуска",         en: "Breakfast", color: "from-amber-500 to-orange-500" },
  lunch:     { icon: ChefHat, bg: "Обяд",            en: "Lunch",     color: "from-emerald-500 to-teal-500" },
  dinner:    { icon: Sunset, bg: "Вечеря",          en: "Dinner",    color: "from-violet-500 to-fuchsia-500" },
  snack:     { icon: Cookie, bg: "Снак",            en: "Snack",     color: "from-rose-500 to-pink-500" },
};

function MealCard({ id, plan, additions, totals, isDark, bg, onAddFood, onRemoveFood, onRemoveOriginal, onSuggest }) {
  const meta = MEAL_META[id];
  if (!meta || !plan) return null;
  const Icon = meta.icon;
  const foods = Array.isArray(plan.foods) ? plan.foods : [];
  const adds = Array.isArray(additions) ? additions : [];
  // Predominantly показваме totals от runtime ако има, иначе оригинала от plan
  const dilShow  = totals?.dil_g  != null ? totals.dil_g  : plan.dil_est_g;
  const nisShow  = totals?.nis_pct != null ? totals.nis_pct : plan.nis_est_pct;
  const uisShow  = totals?.uis_pct != null ? totals.uis_pct : plan.uis_est_pct;
  const kcalShow = totals?.kcal   != null ? totals.kcal   : plan.kcal;
  return (
    <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"} overflow-hidden`}
          data-testid={`menu-meal-${id}`}>
      <CardContent className="p-3.5 space-y-2">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center bg-gradient-to-br ${meta.color} text-white`}>
            <Icon className="w-4 h-4" />
          </div>
          <h4 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? meta.bg : meta.en}
          </h4>
        </div>
        {/* Metrics chips with tooltips — включват runtime totals (с user_additions) */}
        <div className="flex flex-wrap gap-1">
          {dilShow != null && (
            <span title={bg ? "DIL = Dietary Insulin Load (Nutramilo NIS v1.1.9). Очаквана инсулинова експозиция в грама.\nЦел за деня: 45-65g" : "Dietary Insulin Load — expected insulin exposure (g)"}
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold cursor-help ${
                isDark ? "bg-orange-500/20 text-orange-200" : "bg-orange-50 text-orange-700"
              }`}>DIL {dilShow}g</span>
          )}
          {nisShow != null && (
            <span title={bg ? "NIS = Nutramilo Insulin Score v1.1.9 — инсулинов отговор спрямо чиста глюкоза (=100%).\n<35% оптимално · 35-55% умерен · >55% висок" : "Nutramilo Insulin Score — insulin response vs pure glucose"}
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold cursor-help ${
                isDark ? "bg-violet-500/20 text-violet-200" : "bg-violet-50 text-violet-700"
              }`}>NIS {nisShow}%</span>
          )}
          {uisShow != null && (
            <span title={bg ? "UIS = Unified Inflammation Score (EDIP·DII·NEAP·ω).\n0% = противовъзпалително · 100% = провъзпалително" : "Unified Inflammation Score (EDIP·DII·NEAP·ω)"}
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold cursor-help ${
                isDark ? "bg-amber-500/20 text-amber-200" : "bg-amber-50 text-amber-700"
              }`}>UIS {uisShow}%</span>
          )}
          {kcalShow != null && kcalShow > 0 && (
            <span title={bg ? "Атуотъри-калории (4·прот + 4·въглехидрати + 9·мазнини)" : "Atwater calories"}
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold cursor-help ${
                isDark ? "bg-rose-500/20 text-rose-200" : "bg-rose-50 text-rose-700"
              }`}>{Math.round(kcalShow)} kcal</span>
          )}
        </div>
        {/* Foods list (original plan) — iter425: + X бутон за премахване */}
        <ul className={`text-xs space-y-0.5 leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`} data-testid={`menu-original-foods-${id}`}>
          {foods.map((f, i) => {
            if (typeof f === "string") {
              return (
                <li key={i} className="flex items-center justify-between gap-1.5 group">
                  <span>• {f}</span>
                  <button onClick={() => onRemoveOriginal?.(id, i)}
                          data-testid={`menu-remove-original-${id}-${i}`}
                          title={bg ? "Премахни от менюто" : "Remove"}
                          className={`opacity-0 group-hover:opacity-100 w-4 h-4 rounded flex items-center justify-center transition-opacity ${
                            isDark ? "hover:bg-rose-500/20 text-rose-300" : "hover:bg-rose-100 text-rose-600"
                          }`}>
                    <X className="w-3 h-3" />
                  </button>
                </li>
              );
            }
            const grams = f?.grams ? ` — ${Math.round(f.grams)}g` : "";
            return (
              <li key={i} className="flex items-center justify-between gap-1.5 group">
                <span>• {f?.name || ""}<span className={`text-[10.5px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>{grams}</span></span>
                <button onClick={() => onRemoveOriginal?.(id, i)}
                        data-testid={`menu-remove-original-${id}-${i}`}
                        title={bg ? "Премахни от менюто" : "Remove"}
                        className={`opacity-0 group-hover:opacity-100 w-4 h-4 rounded flex items-center justify-center transition-opacity ${
                          isDark ? "hover:bg-rose-500/20 text-rose-300" : "hover:bg-rose-100 text-rose-600"
                        }`}>
                  <X className="w-3 h-3" />
                </button>
              </li>
            );
          })}
        </ul>
        {/* User additions — visible chips with remove */}
        {adds.length > 0 && (
          <div className="space-y-1 pt-1.5 mt-1 border-t border-dashed border-emerald-500/30" data-testid={`menu-additions-${id}`}>
            <p className={`text-[9.5px] uppercase tracking-wider font-bold ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>
              {bg ? "Добавени от Вас" : "Your additions"}
            </p>
            {adds.map((a, idx) => (
              <div key={idx} className={`flex items-center justify-between gap-2 px-2 py-1 rounded-md ${
                isDark ? "bg-emerald-500/10" : "bg-emerald-50"
              }`}>
                <div className="flex-1 min-w-0">
                  <span className={`text-[11px] font-medium ${isDark ? "text-emerald-200" : "text-emerald-900"}`}>
                    {a.name} — {Math.round(a.grams || 0)}g
                  </span>
                  <span className={`ml-1.5 text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {Math.round(a.kcal || 0)} kcal · DIL {a.dil_g} · NIS {a.nis_pct}%
                  </span>
                </div>
                <button onClick={() => onRemoveFood?.(id, idx)}
                        className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center transition-colors ${
                          isDark ? "hover:bg-rose-500/20 text-rose-300" : "hover:bg-rose-100 text-rose-600"
                        }`}
                        data-testid={`menu-remove-food-${id}-${idx}`}
                        title={bg ? "Премахни" : "Remove"}>
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        {plan.rationale_bg && (
          <p className={`text-[10.5px] italic leading-relaxed ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            {plan.rationale_bg}
          </p>
        )}
        {/* iter422 — Action buttons */}
        <div className="flex flex-wrap gap-1.5 pt-1">
          <button onClick={() => onAddFood?.(id)}
                  data-testid={`menu-add-food-btn-${id}`}
                  className={`text-[10.5px] px-2 py-1 rounded-lg font-medium flex items-center gap-1 transition-colors ${
                    isDark ? "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                  }`}>
            <Plus className="w-3 h-3" />{bg ? "Добавяне на храна" : "Add food"}
          </button>
          <button onClick={() => onSuggest?.(id)}
                  data-testid={`menu-suggest-btn-${id}`}
                  className={`text-[10.5px] px-2 py-1 rounded-lg font-medium flex items-center gap-1 transition-colors ${
                    isDark ? "bg-violet-500/20 text-violet-200 hover:bg-violet-500/30" : "bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200"
                  }`}>
            <Lightbulb className="w-3 h-3" />{bg ? "Препоръчай" : "Suggest"}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

/** iter422 — Add/Suggest food picker modal — search local DB + favorites + suggestions */
function AddFoodPicker({ open, onClose, mealKey, isDark, bg, API, favorites, onAdded, suggestionMode = false, suggestions = [], gapBg = "" }) {
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);  // {name, per100g, source}
  const [grams, setGrams] = useState(100);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery(""); setSearchResults([]); setSelectedFood(null);
      setGrams(100); setPreview(null);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open || suggestionMode) return;
    if (!query || query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/food/search?q=${encodeURIComponent(query)}&limit=8`);
        setSearchResults(r.data?.products || []);
      } catch (_e) { setSearchResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(handle);
  }, [query, open, suggestionMode, API]);

  // Preview food score on selection / grams change
  useEffect(() => {
    if (!selectedFood) { setPreview(null); return; }
    setPreviewLoading(true);
    const handle = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/health-intelligence/personalized-menu/lookup-food`, {
          params: { name: selectedFood.name, grams: Number(grams) || 0 },
        });
        if (r.data?.found) setPreview(r.data.score);
        else setPreview(null);
      } catch (_e) { setPreview(null); }
      finally { setPreviewLoading(false); }
    }, 200);
    return () => clearTimeout(handle);
  }, [selectedFood, grams, API]);

  const handleSelect = (item, source) => {
    setSelectedFood({
      name: item.name_bg || item.name || "",
      per100g: item.per100g,
      source,
    });
    if (item.grams_suggested) setGrams(Math.round(item.grams_suggested));
  };

  const handleAdd = async () => {
    if (!selectedFood) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/health-intelligence/personalized-menu/add-food`, {
        meal_key: mealKey,
        food_name: selectedFood.name,
        grams: Number(grams) || 0,
        source_tag: selectedFood.source || "search",
      });
      toast.success(r.data?.verdict_bg || (bg ? "Добавено ✓" : "Added ✓"));
      onAdded?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally { setSubmitting(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm"
         data-testid="menu-add-food-picker"
         onClick={onClose}>
      <div className={`w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col rounded-2xl border ${
        isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"
      }`} onClick={(e) => e.stopPropagation()}>
        <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
          <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            {suggestionMode
              ? (bg ? `Препоръчани храни за ${(MEAL_META[mealKey]?.bg || mealKey)}` : `Suggested foods for ${(MEAL_META[mealKey]?.en || mealKey)}`)
              : (bg ? `Добави храна към ${(MEAL_META[mealKey]?.bg || mealKey)}` : `Add food to ${(MEAL_META[mealKey]?.en || mealKey)}`)}
          </h3>
          <button onClick={onClose} className={`w-7 h-7 rounded-lg flex items-center justify-center ${
            isDark ? "hover:bg-slate-800 text-slate-300" : "hover:bg-slate-100 text-slate-600"
          }`} data-testid="menu-picker-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {suggestionMode && gapBg && (
            <p className={`text-[11px] leading-relaxed p-2 rounded-lg ${
              isDark ? "bg-violet-500/15 text-violet-100" : "bg-violet-50 text-violet-900"
            }`}>
              <Lightbulb className="inline w-3 h-3 mr-1" />{gapBg}
            </p>
          )}

          {/* Suggestion mode list */}
          {suggestionMode && (
            <div className="space-y-1.5">
              {(suggestions || []).map((s, i) => (
                <button key={i} onClick={() => handleSelect({
                  name_bg: s.name, grams_suggested: s.grams_suggested,
                  per100g: null,
                }, "suggestion")}
                  data-testid={`menu-suggestion-${i}`}
                  className={`w-full text-left p-2 rounded-lg border transition-colors ${
                    selectedFood?.name === s.name
                      ? (isDark ? "border-emerald-400 bg-emerald-500/15" : "border-emerald-500 bg-emerald-50")
                      : (isDark ? "border-slate-700 hover:bg-slate-800" : "border-slate-200 hover:bg-slate-50")
                  }`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {s.name}<span className={`ml-1 text-[10px] font-normal ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        — {s.grams_suggested}g
                      </span>
                    </span>
                    <span className={`text-[9.5px] px-1.5 py-0.5 rounded-full ${
                      isDark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"
                    }`}>{s.tag}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    <span className={`text-[10px] ${isDark ? "text-orange-300" : "text-orange-700"}`}>{s.kcal} kcal</span>
                    <span className={`text-[10px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>· DIL {s.dil_g}g</span>
                    <span className={`text-[10px] ${isDark ? "text-violet-300" : "text-violet-700"}`}>· NIS {s.nis_pct}%</span>
                    <span className={`text-[10px] ${isDark ? "text-amber-300" : "text-amber-700"}`}>· UIS {s.uis_pct}%</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Search mode */}
          {!suggestionMode && (
            <>
              {/* Favorites quick-pick */}
              {favorites?.length > 0 && (
                <div className="space-y-1">
                  <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>
                    <Heart className="inline w-3 h-3 mr-1" />{bg ? "От Вашите любими" : "From favorites"}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {favorites.slice(0, 12).map((f, i) => (
                      <button key={i} onClick={() => handleSelect({ name_bg: f, per100g: null }, "favorite")}
                              data-testid={`menu-favorite-pill-${i}`}
                              className={`text-[11px] px-2 py-1 rounded-full transition-colors ${
                                selectedFood?.name === f
                                  ? (isDark ? "bg-emerald-500 text-white" : "bg-emerald-500 text-white")
                                  : (isDark ? "bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25" : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100")
                              }`}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Search input */}
              <div className="space-y-1">
                <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  <Search className="inline w-3 h-3 mr-1" />{bg ? "Или потърси нова" : "Or search new"}
                </p>
                <Input value={query} onChange={(e) => setQuery(e.target.value)}
                       placeholder={bg ? "напр. сьомга, броколи, киноа…" : "e.g. salmon, broccoli, quinoa…"}
                       data-testid="menu-add-food-search-input"
                       className={`text-xs ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`} />
                {searching && (
                  <div className={`text-[10px] flex items-center gap-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    <Loader2 className="w-3 h-3 animate-spin" />{bg ? "Търся…" : "Searching…"}
                  </div>
                )}
                {searchResults.length > 0 && (
                  <div className={`max-h-44 overflow-y-auto rounded-lg border ${isDark ? "border-slate-700" : "border-slate-200"}`}>
                    {searchResults.map((item, i) => {
                      const label = item.name_bg || item.name || "—";
                      return (
                        <button key={i} onClick={() => handleSelect(item, item.source || "search")}
                                data-testid={`menu-search-result-${i}`}
                                className={`w-full text-left px-2 py-1.5 text-[11px] border-b last:border-b-0 transition-colors ${
                                  isDark ? "border-slate-800 hover:bg-slate-800" : "border-slate-100 hover:bg-slate-50"
                                } ${selectedFood?.name === label ? (isDark ? "bg-emerald-500/15" : "bg-emerald-50") : ""}`}>
                          <span className={isDark ? "text-white" : "text-slate-900"}>{label}</span>
                          {item.per100g?.calories ? (
                            <span className={`ml-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>· {Math.round(item.per100g.calories)} kcal/100g</span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Grams + preview block */}
          {selectedFood && (
            <div className={`p-3 rounded-xl border space-y-2 ${
              isDark ? "border-emerald-500/30 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50/40"
            }`} data-testid="menu-add-food-preview">
              <div className="flex items-center justify-between gap-2">
                <span className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {selectedFood.name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <label className={`text-[10.5px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  {bg ? "Количество (g)" : "Amount (g)"}
                </label>
                <Input type="number" value={grams} min={1} max={2000}
                       onChange={(e) => setGrams(Math.max(1, Number(e.target.value) || 1))}
                       data-testid="menu-add-food-grams"
                       className={`w-24 h-7 text-xs ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`} />
              </div>
              {previewLoading ? (
                <div className={`text-[10px] flex items-center gap-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  <Loader2 className="w-3 h-3 animate-spin" />{bg ? "Изчислявам…" : "Computing…"}
                </div>
              ) : preview ? (
                <div className="grid grid-cols-4 gap-1.5 text-center" data-testid="menu-add-food-metrics">
                  <div className={`p-1.5 rounded ${isDark ? "bg-rose-500/15" : "bg-rose-50"}`}>
                    <p className={`text-[9px] uppercase ${isDark ? "text-rose-300" : "text-rose-700"}`}>kcal</p>
                    <p className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{Math.round(preview.kcal || 0)}</p>
                  </div>
                  <div className={`p-1.5 rounded ${isDark ? "bg-orange-500/15" : "bg-orange-50"}`}>
                    <p className={`text-[9px] uppercase ${isDark ? "text-orange-300" : "text-orange-700"}`}>DIL</p>
                    <p className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{preview.dil_g}g</p>
                  </div>
                  <div className={`p-1.5 rounded ${isDark ? "bg-violet-500/15" : "bg-violet-50"}`}>
                    <p className={`text-[9px] uppercase ${isDark ? "text-violet-300" : "text-violet-700"}`}>NIS</p>
                    <p className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{preview.nis_pct}%</p>
                  </div>
                  <div className={`p-1.5 rounded ${isDark ? "bg-amber-500/15" : "bg-amber-50"}`}>
                    <p className={`text-[9px] uppercase ${isDark ? "text-amber-300" : "text-amber-700"}`}>UIS</p>
                    <p className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{preview.uis_pct}%</p>
                  </div>
                </div>
              ) : (
                <p className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                  {bg ? "Не намерих храната в локалната база — ще използвам приближена оценка." : "Not in local DB — will use estimate."}
                </p>
              )}
            </div>
          )}
        </div>

        <div className={`px-3 py-2.5 border-t flex gap-2 ${isDark ? "border-slate-800 bg-slate-900/80" : "border-slate-200 bg-slate-50/80"}`}>
          <Button variant="ghost" onClick={onClose} className="flex-1" data-testid="menu-picker-cancel">
            {bg ? "Откажи" : "Cancel"}
          </Button>
          <Button onClick={handleAdd} disabled={!selectedFood || submitting}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
                  data-testid="menu-picker-confirm">
            {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
            {bg ? "Добави в менюто" : "Add to menu"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FavoriteFoodRow({ item, isDark, bg, API }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const handleSave = async () => {
    if (saving || saved) return;
    setSaving(true);
    try {
      await axios.post(`${API}/health-intelligence/snapshot`, {
        snapshot_type: "favorite_food",
        title: item.name_bg,
        summary: item.reason_bg,
        payload: { food_name: item.name_bg, source: "personalized_menu" },
        tags: ["personalized-menu", "favor"],
      });
      setSaved(true);
      toast.success(bg ? "Запазено ❤" : "Saved ❤");
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className={`flex items-start gap-2 p-2.5 rounded-xl border ${isDark ? "border-emerald-500/20 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50/40"}`}>
      <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-bold ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>{item.name_bg}</p>
        <p className={`text-[10.5px] leading-relaxed ${isDark ? "text-emerald-200/80" : "text-emerald-900/70"}`}>{item.reason_bg}</p>
      </div>
      <button onClick={handleSave} disabled={saving || saved}
              data-testid={`menu-favor-save-${(item.name_bg || "").slice(0, 20)}`}
              className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                saved ? "bg-emerald-500 text-white" : (isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700" : "bg-white text-slate-500 hover:bg-slate-50")
              }`}>
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bookmark className="w-3.5 h-3.5" fill={saved ? "currentColor" : "none"} />}
      </button>
    </div>
  );
}

export default function PersonalizedMenuCard() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activating, setActivating] = useState(false);
  const [preferences, setPreferences] = useState("");
  const [progress, setProgress] = useState(null);

  // iter422 — Add/Suggest food picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMeal, setPickerMeal] = useState("breakfast");
  const [pickerSuggestionMode, setPickerSuggestionMode] = useState(false);
  const [pickerSuggestions, setPickerSuggestions] = useState([]);
  const [pickerGapBg, setPickerGapBg] = useState("");
  const [favorites, setFavorites] = useState([]);  // BG name strings

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/health-intelligence/personalized-menu/latest`);
      if (r.data?.exists) setMenu(r.data);
    } catch (_e) {}
    finally { setLoading(false); }
  }, [API]);

  const fetchProgress = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/health-intelligence/personalized-menu/weekly-progress`);
      if (r.data?.has_menu) setProgress(r.data);
    } catch (_e) {}
  }, [API]);

  // iter422 — Fetch favorites for quick-pick
  const fetchFavorites = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/health-intelligence/snapshots?days=90&type=favorite_food&limit=30`);
      const items = r.data?.items || [];
      const names = [];
      for (const it of items) {
        // Skip label-style snapshots (lab panels, plate sims) — only real foods
        const foodName = it.payload?.food_name;
        if (!foodName) continue;
        // Extract BG part from "EN / BG" format
        const bgName = foodName.includes(" / ") ? foodName.split(" / ").slice(-1)[0].trim() : foodName;
        // Strip parentheses and clean
        const cleaned = bgName.replace(/\(.*?\)/g, "").replace(/[,]+\s*$/, "").trim();
        if (cleaned && cleaned.length >= 2 && cleaned.length <= 50) names.push(cleaned);
      }
      // Dedupe (case-insensitive)
      const seen = new Set();
      const unique = [];
      for (const n of names) {
        const k = n.toLowerCase();
        if (!seen.has(k)) { seen.add(k); unique.push(n); }
      }
      setFavorites(unique);
    } catch (_e) {}
  }, [API]);

  useEffect(() => {
    fetchLatest();
    fetchProgress();
    fetchFavorites();
  }, [fetchLatest, fetchProgress, fetchFavorites]);

  const handleGenerate = async (forceRefresh = false) => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/health-intelligence/personalized-menu`, {
        preferences: preferences.trim() || undefined,
        force_refresh: forceRefresh,
      });
      setMenu(r.data);
      toast.success(bg ? "Менюто е генерирано ✓" : "Menu generated ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка при генерация" : "Generation failed"));
    } finally {
      setGenerating(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      const r = await axios.post(`${API}/health-intelligence/personalized-menu/activate`);
      toast.success(r.data?.message_bg || (bg ? "Активирано ✓" : "Activated ✓"));
      // Refresh menu state to update activated_at indicator
      fetchLatest();
      fetchProgress();
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally {
      setActivating(false);
    }
  };

  // iter422 — Open picker for adding food
  const handleOpenAddFood = (mealKey) => {
    setPickerMeal(mealKey);
    setPickerSuggestionMode(false);
    setPickerSuggestions([]);
    setPickerGapBg("");
    setPickerOpen(true);
  };

  // iter422 — Open picker for suggestions
  const handleOpenSuggest = async (mealKey) => {
    setPickerMeal(mealKey);
    setPickerSuggestionMode(true);
    setPickerGapBg("");
    setPickerOpen(true);
    try {
      const r = await axios.post(`${API}/health-intelligence/personalized-menu/suggest-foods`, {
        meal_key: mealKey, goal: "dil_to_target",
      });
      setPickerSuggestions(r.data?.suggestions || []);
      setPickerGapBg(r.data?.gap_summary_bg || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    }
  };

  // iter422 — When food is added → update menu state in-place with runtime totals
  const handleFoodAdded = (resp) => {
    if (!resp || !menu) return;
    setMenu((m) => ({
      ...m,
      user_additions: {
        ...(m.user_additions || {}),
        [resp.meal_key]: [...((m.user_additions || {})[resp.meal_key] || []), resp.added_food],
      },
      meal_totals_runtime: resp.all_meals_totals,
      daily_runtime: resp.daily_totals_runtime,
    }));
  };

  // iter422 — Remove food
  const handleRemoveFood = async (mealKey, foodIndex) => {
    try {
      const r = await axios.post(`${API}/health-intelligence/personalized-menu/remove-food`, {
        meal_key: mealKey, food_index: foodIndex,
      });
      setMenu((m) => ({
        ...m,
        user_additions: {
          ...(m.user_additions || {}),
          [mealKey]: ((m.user_additions || {})[mealKey] || []).filter((_, i) => i !== foodIndex),
        },
        meal_totals_runtime: r.data?.all_meals_totals,
        daily_runtime: r.data?.daily_totals_runtime,
      }));
      toast.success(bg ? "Премахнато ✓" : "Removed ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    }
  };

  // iter425 — Remove ORIGINAL food (от AI-генерираните)
  const handleRemoveOriginal = async (mealKey, foodIndex) => {
    try {
      const r = await axios.post(`${API}/health-intelligence/personalized-menu/remove-original-food`, {
        meal_key: mealKey, food_index: foodIndex,
      });
      setMenu((m) => ({
        ...m,
        meal_plan: {
          ...(m.meal_plan || {}),
          [mealKey]: r.data?.meal_plan_updated || m.meal_plan?.[mealKey],
        },
        meal_totals_runtime: r.data?.all_meals_totals,
        daily_runtime: r.data?.daily_totals_runtime,
      }));
      toast.success(bg ? "Храната е премахната ✓" : "Food removed ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    }
  };

  const hasMenu = menu && menu.meal_plan && Object.keys(menu.meal_plan).length > 0;

  // Compute effective daily totals (runtime preferred над оригинала)
  const effectiveDaily = (menu?.daily_runtime) || (menu?.daily_totals) || {};
  const runtimeMealTotals = menu?.meal_totals_runtime || {};

  return (
    <Card className={`border ${isDark ? "border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-teal-500/5" : "border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-teal-50/30"}`}
          data-testid="personalized-menu-card">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex-shrink-0">
            <ChefHat className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Персонализирано меню — NutriBot + EndoBot" : "Personalized menu — NutriBot + EndoBot"}
            </h3>
            <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg
                ? "Дневен план, генериран от целия ти профил — заболявания, метаболитни средни, сън и спорт."
                : "Daily plan generated from your full profile — conditions, metabolic averages, sleep and sport."}
            </p>
          </div>
          {hasMenu && (
            <Button onClick={() => handleGenerate(true)} disabled={generating}
                    variant="ghost" size="sm" className="h-7 px-2" data-testid="menu-regenerate">
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 gap-2">
            <Loader2 className={`w-5 h-5 animate-spin ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
            <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {bg ? "Зареждам последното меню…" : "Loading latest menu…"}
            </span>
          </div>
        ) : !hasMenu ? (
          /* Empty state — generate CTA */
          <div className="space-y-3" data-testid="menu-empty-state">
            <div className={`p-3 rounded-xl border-dashed border-2 ${isDark ? "border-emerald-500/30 bg-emerald-500/5" : "border-emerald-200 bg-white/60"}`}>
              <p className={`text-xs leading-relaxed ${isDark ? "text-emerald-100/90" : "text-emerald-900/90"}`}>
                {bg
                  ? "Все още няма генерирано меню. Натисни 'Генерирай' за да получиш холистичен план на база целия си профил."
                  : "No menu yet. Click 'Generate' for a holistic plan based on your full profile."}
              </p>
            </div>
            <div className="space-y-2">
              <label className={`text-[10.5px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Предпочитания (опц.)" : "Preferences (optional)"}
              </label>
              <Input
                value={preferences}
                onChange={(e) => setPreferences(e.target.value)}
                placeholder={bg ? "Веган · Без глутен · Не обичам риба…" : "Vegan · Gluten-free · No fish…"}
                className={`${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}
                maxLength={200}
                data-testid="menu-preferences"
              />
            </div>
            <Button onClick={() => handleGenerate(false)} disabled={generating}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                    data-testid="menu-generate-btn">
              {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
              {generating
                ? (bg ? "AI генерира меню (~10 сек)…" : "AI generating (~10s)…")
                : (bg ? "Генерирай меню" : "Generate menu")}
            </Button>
          </div>
        ) : (
          /* Menu rendered */
          <div className="space-y-4" data-testid="menu-content">
            {/* Summary */}
            {menu.summary_bg && (
              <div className={`p-3 rounded-xl ${isDark ? "bg-slate-900/60 border border-slate-800" : "bg-white border border-slate-200"}`}>
                <p className={`text-xs leading-relaxed ${isDark ? "text-slate-200" : "text-slate-800"}`}>
                  {menu.summary_bg}
                </p>
                {/* iter424 — Cycle phase badge */}
                {menu.cycle_phase?.phase_name_bg && (
                  <div className={`mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    menu.cycle_phase.phase_color === "rose"    ? (isDark ? "bg-rose-500/20 text-rose-200"     : "bg-rose-50 text-rose-700") :
                    menu.cycle_phase.phase_color === "emerald" ? (isDark ? "bg-emerald-500/20 text-emerald-200": "bg-emerald-50 text-emerald-700") :
                    menu.cycle_phase.phase_color === "amber"   ? (isDark ? "bg-amber-500/20 text-amber-200"   : "bg-amber-50 text-amber-700") :
                    menu.cycle_phase.phase_color === "violet"  ? (isDark ? "bg-violet-500/20 text-violet-200" : "bg-violet-50 text-violet-700") :
                    (isDark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600")
                  }`} data-testid="menu-cycle-badge">
                    <span>{menu.cycle_phase.phase_icon || "🌗"}</span>
                    {bg ? "Адаптирано за фаза" : "Tuned for phase"}: <strong>{menu.cycle_phase.phase_name_bg}</strong>
                    {menu.cycle_phase.cycle_day && <span>· ден {menu.cycle_phase.cycle_day}</span>}
                    {menu.cycle_phase.dil_adjustment !== 0 && menu.cycle_phase.dil_adjustment != null && (
                      <span>· DIL Δ {menu.cycle_phase.dil_adjustment > 0 ? "+" : ""}{menu.cycle_phase.dil_adjustment}g</span>
                    )}
                  </div>
                )}
                {/* iter535 — Perinatal mode badge — show WHY macros differ */}
                {menu.perinatal?.active && (
                  <div
                    className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                      isDark ? "bg-pink-500/15 text-pink-200 ring-1 ring-pink-500/40" : "bg-pink-50 text-pink-700 ring-1 ring-pink-200"
                    }`}
                    title={
                      bg
                        ? `Менюто е генерирано в перинатален режим — DIL таванът е персонализиран спрямо ${menu.perinatal.stage_label_bg || menu.perinatal.stage} и ${(menu.perinatal.conditions || []).length} състояния.`
                        : `Menu generated in perinatal mode — DIL ceiling personalised for ${menu.perinatal.stage} and ${(menu.perinatal.conditions || []).length} conditions.`
                    }
                    data-testid="menu-perinatal-badge"
                  >
                    <span>🤰</span>
                    {bg ? "Перинатален режим" : "Perinatal mode"}:
                    <strong>{menu.perinatal.stage_label_bg || menu.perinatal.stage}</strong>
                    <span>· DIL ≤ {menu.perinatal.ceiling_g}g</span>
                    {(menu.perinatal.conditions || []).length > 0 && (
                      <span className="opacity-70">
                        · {(menu.perinatal.conditions || []).map(c => bg ? c.label_bg : c.key).join(", ")}
                      </span>
                    )}
                  </div>
                )}
                {menu.from_cache && menu.cache_age_hours != null && (
                  <p className={`text-[10px] mt-2 flex items-center gap-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                    <Clock className="w-3 h-3" />
                    {bg
                      ? `Кеширано преди ${menu.cache_age_hours}ч · Натисни ↻ за ново`
                      : `Cached ${menu.cache_age_hours}h ago · Click ↻ for fresh`}
                  </p>
                )}
              </div>
            )}

            {/* Daily totals strip (DIL + NIS + UIS + kcal) — с tooltips и runtime totals */}
            {(menu.daily_totals || effectiveDaily) && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="menu-daily-totals">
                <div title={bg ? "DIL ОЦЕНКА за това меню (g) — сума от планираните хранения.\n\nЗабележка: Това е изчислена стойност за избраното меню, не фиксиран дневен бюджет.\nГлобалният 'DIL — Инсулинов товар' в горната част на страницата е твоят ЛИЧЕН бюджет/цел (45-65g/ден).\n\nС добавяне/премахване на храни тази оценка се променя в реално време." : "DIL ESTIMATE for this menu (g) — sum of planned meals.\n\nNote: This is the computed value for the selected menu, NOT a fixed daily budget. The global 'DIL' KPI at the top of the page is your personal target (45-65g/day).\n\nThis estimate updates in real-time as you add/remove foods."}
                     className={`p-2.5 rounded-xl text-center cursor-help ${isDark ? "bg-orange-500/15 border border-orange-500/30" : "bg-orange-50 border border-orange-200"}`}
                     data-testid="menu-daily-dil-tile">
                  <Flame className={`w-3.5 h-3.5 mx-auto ${isDark ? "text-orange-300" : "text-orange-600"}`} />
                  <p className={`text-[10px] uppercase tracking-wider font-bold mt-1 ${isDark ? "text-orange-300" : "text-orange-700"}`}>{bg ? "DIL (оценка)" : "DIL (est.)"}</p>
                  <p className={`text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{effectiveDaily.dil_g ?? menu.daily_totals?.dil_g ?? "—"}<span className="text-[10px] font-medium ml-0.5">g</span></p>
                </div>
                <div title={bg ? "NIS — Nutramilo Insulin Score v1.1.9. Среден инсулинов отговор спрямо чиста глюкоза.\n<35% оптимално · >55% висок." : "Nutramilo Insulin Score v1.1.9"}
                     className={`p-2.5 rounded-xl text-center cursor-help ${isDark ? "bg-violet-500/15 border border-violet-500/30" : "bg-violet-50 border border-violet-200"}`}>
                  <Activity className={`w-3.5 h-3.5 mx-auto ${isDark ? "text-violet-300" : "text-violet-600"}`} />
                  <p className={`text-[10px] uppercase tracking-wider font-bold mt-1 ${isDark ? "text-violet-300" : "text-violet-700"}`}>{bg ? "NIS среден" : "NIS avg"}</p>
                  <p className={`text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{effectiveDaily.nis_pct_avg ?? menu.daily_totals?.nis_pct_avg ?? "—"}<span className="text-[10px] font-medium ml-0.5">%</span></p>
                </div>
                <div title={bg ? "UIS — Unified Inflammation Score (EDIP·DII·NEAP·ω).\n0% = противовъзпалително · 100% = провъзпалително." : "Unified Inflammation Score"}
                     className={`p-2.5 rounded-xl text-center cursor-help ${isDark ? "bg-amber-500/15 border border-amber-500/30" : "bg-amber-50 border border-amber-200"}`}>
                  <Zap className={`w-3.5 h-3.5 mx-auto ${isDark ? "text-amber-300" : "text-amber-600"}`} />
                  <p className={`text-[10px] uppercase tracking-wider font-bold mt-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>{bg ? "UIS среден" : "UIS avg"}</p>
                  <p className={`text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{effectiveDaily.uis_pct_avg ?? menu.daily_totals?.uis_pct_avg ?? "—"}<span className="text-[10px] font-medium ml-0.5">%</span></p>
                </div>
                {/* iter422 — kcal tile */}
                <div title={bg ? "Дневни калории — Atwater формула: 4·прот + 4·въглехидрати + 9·мазнини.\nЦел за умерено активен възрастен: 1800-2200 kcal." : "Daily calories (Atwater)"}
                     className={`p-2.5 rounded-xl text-center cursor-help ${isDark ? "bg-rose-500/15 border border-rose-500/30" : "bg-rose-50 border border-rose-200"}`}
                     data-testid="menu-daily-kcal">
                  <Flame className={`w-3.5 h-3.5 mx-auto ${isDark ? "text-rose-300" : "text-rose-600"}`} />
                  <p className={`text-[10px] uppercase tracking-wider font-bold mt-1 ${isDark ? "text-rose-300" : "text-rose-700"}`}>{bg ? "Калории" : "Calories"}</p>
                  <p className={`text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {effectiveDaily.kcal ?? menu.daily_totals?.kcal ?? "—"}<span className="text-[10px] font-medium ml-0.5">kcal</span>
                  </p>
                </div>
              </div>
            )}
            {/* Method footer for transparency */}
            <p className={`text-[10px] italic ${isDark ? "text-slate-500" : "text-slate-500"}`} data-testid="menu-method-note">
              {bg
                ? "DIL и NIS изчислени с независимо валидираната Nutramilo NIS v1.1.9 формула. UIS използва EDIP·DII·NEAP·ω рамка."
                : "DIL & NIS computed with independently validated Nutramilo NIS v1.1.9 formula. UIS uses EDIP·DII·NEAP·ω framework."}
            </p>

            {/* Meal plan grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["breakfast", "lunch", "dinner", "snack"].map((mk) => (
                <MealCard
                  key={mk}
                  id={mk}
                  plan={menu.meal_plan[mk]}
                  additions={(menu.user_additions || {})[mk]}
                  totals={runtimeMealTotals[mk]}
                  isDark={isDark}
                  bg={bg}
                  onAddFood={handleOpenAddFood}
                  onRemoveFood={handleRemoveFood}
                  onRemoveOriginal={handleRemoveOriginal}
                  onSuggest={handleOpenSuggest}
                />
              ))}
            </div>

            {/* Foods to favor */}
            {Array.isArray(menu.foods_to_favor) && menu.foods_to_favor.length > 0 && (
              <div className="space-y-2">
                <h4 className={`text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                  <Heart className="w-3.5 h-3.5" />
                  {bg ? "Храни за предпочитане" : "Foods to favor"}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {menu.foods_to_favor.map((f, i) => (
                    <FavoriteFoodRow key={i} item={f} isDark={isDark} bg={bg} API={API} />
                  ))}
                </div>
              </div>
            )}

            {/* Foods to limit */}
            {Array.isArray(menu.foods_to_limit) && menu.foods_to_limit.length > 0 && (
              <div className="space-y-2">
                <h4 className={`text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                  <Hand className="w-3.5 h-3.5" />
                  {bg ? "Храни за ограничаване" : "Foods to limit"}
                </h4>
                <div className="space-y-1.5">
                  {menu.foods_to_limit.map((f, i) => (
                    <div key={i}
                         data-testid={`menu-limit-${i}`}
                         className={`p-2.5 rounded-xl border flex items-start gap-2 ${
                           isDark ? "border-amber-500/20 bg-amber-500/5" : "border-amber-200 bg-amber-50/40"
                         }`}>
                      <Hand className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? "text-amber-300" : "text-amber-600"}`} />
                      <div>
                        <p className={`text-xs font-bold ${isDark ? "text-amber-100" : "text-amber-900"}`}>{f.name_bg}</p>
                        <p className={`text-[10.5px] leading-relaxed ${isDark ? "text-amber-200/80" : "text-amber-900/70"}`}>{f.reason_bg}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* iter1034 — железен преглед: кафето и млечното да не падат върху желязото */}
            {menu.iron_review && (
              <div className={`p-3 rounded-xl border-l-4 ${
                menu.iron_review.conflicts?.length
                  ? (isDark ? "border-l-rose-400 bg-rose-500/5" : "border-l-rose-500 bg-rose-50/40")
                  : (isDark ? "border-l-emerald-400 bg-emerald-500/5" : "border-l-emerald-500 bg-emerald-50/40")}`}
                data-testid="menu-iron-review">
                <p className={`text-[10.5px] uppercase tracking-wider font-bold ${isDark ? "text-rose-300" : "text-rose-700"}`}>
                  Желязо и тайминг
                </p>
                <p className={`text-xs mt-1 ${isDark ? "text-slate-200" : "text-slate-800"}`}
                  data-testid="menu-iron-summary">
                  {menu.iron_review.summary_bg}
                </p>
                {menu.iron_review.need_reasons_bg?.map((r, i) => (
                  <p key={i} className="text-[10.5px] opacity-75 mt-0.5">· {r}</p>
                ))}
                {menu.iron_review.conflicts?.map((c, i) => (
                  <div key={i} className="mt-1.5 text-[11px]" data-testid={`menu-iron-conflict-${c.meal}`}>
                    <span className="font-bold">{c.meal_bg}: {c.blockers_bg.join(", ")}</span>
                    <span className="opacity-80"> — {c.fix_bg}</span>
                  </div>
                ))}
                {menu.iron_review.missing_vitc_bg?.length > 0 && (
                  <p className="text-[10.5px] opacity-80 mt-1">
                    Добавете витамин C (чушка, лимон, домат) в: {menu.iron_review.missing_vitc_bg.join(", ")}.
                  </p>
                )}
                <p className="text-[9.5px] opacity-60 mt-1">{menu.iron_review.note_bg}</p>
              </div>
            )}

            {/* Weekly experiment */}
            {menu.weekly_experiment_bg && (
              <div className={`p-3 rounded-xl border-l-4 ${isDark ? "border-l-violet-400 bg-violet-500/5" : "border-l-violet-500 bg-violet-50/40"}`}
                   data-testid="menu-weekly-experiment">
                <p className={`text-[10.5px] uppercase tracking-wider font-bold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                  {bg ? "Експеримент за тази седмица" : "This week's experiment"}
                </p>
                <p className={`text-xs leading-relaxed mt-1 ${isDark ? "text-slate-200" : "text-slate-800"}`}>
                  {menu.weekly_experiment_bg}
                </p>
              </div>
            )}

            {/* EndoBot panel */}
            {menu.endocrine && (menu.endocrine.priorities_bg?.length > 0 || menu.endocrine.timing_advice_bg) && (
              <div className={`p-3 rounded-xl border ${isDark ? "border-sky-500/20 bg-sky-500/5" : "border-sky-200 bg-sky-50/40"}`}
                   data-testid="menu-endobot-panel">
                <div className="flex items-center gap-2 mb-2">
                  <Stethoscope className={`w-4 h-4 ${isDark ? "text-sky-300" : "text-sky-600"}`} />
                  <h4 className={`text-xs uppercase tracking-wider font-bold ${isDark ? "text-sky-300" : "text-sky-700"}`}>
                    {bg ? "EndoBot — ендокринен прочит" : "EndoBot — endocrine review"}
                  </h4>
                </div>
                {Array.isArray(menu.endocrine.priorities_bg) && menu.endocrine.priorities_bg.length > 0 && (
                  <ul className={`text-[11.5px] space-y-0.5 leading-relaxed mb-2 ${isDark ? "text-slate-200" : "text-slate-800"}`}>
                    {menu.endocrine.priorities_bg.map((p, i) => <li key={i}>• {p}</li>)}
                  </ul>
                )}
                {menu.endocrine.timing_advice_bg && (
                  <p className={`text-[11px] mb-1.5 leading-relaxed ${isDark ? "text-sky-100" : "text-sky-900"}`}>
                    <Clock className="inline w-3 h-3 mr-1" />{menu.endocrine.timing_advice_bg}
                  </p>
                )}
                {Array.isArray(menu.endocrine.food_adjustments_bg) && menu.endocrine.food_adjustments_bg.length > 0 && (
                  <ul className={`text-[10.5px] space-y-0.5 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    {menu.endocrine.food_adjustments_bg.map((a, i) => (
                      <li key={i}>• <strong>{a.adjustment}</strong> — {a.reason}</li>
                    ))}
                  </ul>
                )}
                {menu.endocrine.lab_to_monitor_bg && (
                  <p className={`text-[10.5px] mt-2 flex items-start gap-1 ${isDark ? "text-sky-300/80" : "text-sky-700/80"}`}>
                    <FlaskConical className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{bg ? "За проследяване:" : "Worth monitoring:"} {menu.endocrine.lab_to_monitor_bg}</span>
                  </p>
                )}
              </div>
            )}

            {/* iter421.6 — Activate menu CTA */}
            {!menu.activated_at && (
              <div className="pt-1">
                <Button onClick={handleActivate} disabled={activating}
                        className="w-full bg-gradient-to-r from-violet-500 to-emerald-500 hover:from-violet-600 hover:to-emerald-600 text-white"
                        data-testid="menu-activate-btn">
                  {activating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                  {bg ? "Активирай менюто (запази храните като любими)" : "Activate menu (save favorites)"}
                </Button>
              </div>
            )}

            {/* iter421.6/8 — Weekly progress with menu target + personal DIL */}
            {progress && progress.has_menu && (
              <div className={`p-3 rounded-xl border ${isDark ? "border-emerald-500/30 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50/40"}`}
                   data-testid="menu-weekly-progress">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className={`w-4 h-4 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
                  <h4 className={`text-xs uppercase tracking-wider font-bold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                    {bg ? "Седмичен напредък" : "Weekly progress"}
                  </h4>
                  <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-bold ${
                        progress.completion_pct >= 70
                          ? (isDark ? "bg-emerald-500/30 text-emerald-200" : "bg-emerald-100 text-emerald-700")
                          : (isDark ? "bg-amber-500/30 text-amber-200" : "bg-amber-100 text-amber-700")
                      }`}>
                    {progress.completion_pct}%
                  </span>
                </div>
                <div className={`w-full h-2 rounded-full overflow-hidden mb-2 ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all"
                       style={{width: `${progress.completion_pct}%`}} />
                </div>
                <div className={`grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2 text-[10.5px] ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  <div>
                    <span className="opacity-60 block">{bg ? "Хранения / лог дни" : "Meals / logged days"}</span>
                    <strong>{progress.actual_logged_meals}</strong> / {progress.planned_meals}
                    {progress.distinct_days_logged != null && (
                      <span className="opacity-60"> ({progress.distinct_days_logged}{bg ? "д" : "d"})</span>
                    )}
                  </div>
                  <div>
                    <span className="opacity-60 block" title={bg ? "Среден дневен DIL — сума на DIL от всички хранения в деня, осреднено по логнатите дни." : "Average daily DIL — sum of meals per day, averaged."}>
                      {bg ? "Реален дневен DIL" : "Actual daily DIL"}
                    </span>
                    <strong>{progress.dil_actual_daily_avg != null ? `${progress.dil_actual_daily_avg}g` : "—"}</strong>
                  </div>
                  <div>
                    <span className="opacity-60 block" title={bg ? "Цел от менюто (lean) — обикновено по-ниска от личния DIL за активна метаболитна корекция." : "Menu target (lean) — typically lower than personal."}>
                      {bg ? "Цел от менюто" : "Menu target"}
                    </span>
                    <strong>{progress.dil_menu_target_g}g</strong>
                    {progress.dil_personal_target_g != null && (
                      <div className="opacity-60 text-[10px]">
                        {bg ? "Личен:" : "Personal:"} {Math.round(progress.dil_personal_target_g)}g
                      </div>
                    )}
                  </div>
                </div>
                <p className={`text-[11px] leading-relaxed ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>
                  {progress.verdict_bg}
                </p>
              </div>
            )}

            {/* Disclaimer */}
            {menu.disclaimer_bg && (
              <p className={`text-[10px] italic flex items-start gap-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span>{menu.disclaimer_bg}</span>
              </p>
            )}
          </div>
        )}
      </CardContent>
      {/* iter422 — Add/Suggest food picker */}
      <AddFoodPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        mealKey={pickerMeal}
        isDark={isDark}
        bg={bg}
        API={API}
        favorites={favorites}
        onAdded={handleFoodAdded}
        suggestionMode={pickerSuggestionMode}
        suggestions={pickerSuggestions}
        gapBg={pickerGapBg}
      />
    </Card>
  );
}
